/**
 * Каталог шрифтов.
 *
 * Все файлы лежат в public/fonts и поставляются вместе с приложением:
 * ни одного обращения к Google Fonts CDN в runtime нет.
 *
 * Для каждого начертания хранятся два файла:
 *   woff2 — для @font-face в интерфейсе и предпросмотре;
 *   ttf   — для opentype.js, который переводит подпись в контуры при экспорте.
 * Это один и тот же дизайн одного и того же статического начертания:
 * переменные шрифты заранее инстанцированы (см. scripts/fetch-fonts.py).
 *
 * revision — короткий sha256 ttf-файла. Он пишется в сохранённый проект,
 * чтобы при несовпадении версии шрифта это было видно, а не проявлялось
 * молча съехавшей вёрсткой.
 */
export type FontFace = {
  weight: number;
  /** Человеческое имя начертания для интерфейса. */
  label: string;
  woff2: string;
  ttf: string;
  revision: string;
};

export type FontFamily = {
  id: string;
  /** Имя семейства, как его показываем пользователю. */
  name: string;
  /** Значение font-family в CSS (у нас совпадает с именем). */
  cssFamily: string;
  /** Для чего это семейство задумано — подсказка в интерфейсе. */
  hint: string;
  /** Запасные семейства на случай, если локальный файл не загрузился. */
  fallback: string;
  faces: FontFace[];
  license: 'OFL-1.1';
  licenseFile: string;
};

const base = 'fonts/';

export const FONT_FAMILIES: FontFamily[] = [
  {
    id: 'golos-text',
    name: 'Golos Text',
    cssFamily: 'Golos Text',
    hint: 'Основной вариант: подписи и интерфейс',
    fallback: 'system-ui, sans-serif',
    license: 'OFL-1.1',
    licenseFile: 'licenses/OFL-golos-text.txt',
    faces: [
      {
        weight: 400,
        label: 'Обычное',
        woff2: `${base}golos-text-400.woff2`,
        ttf: `${base}golos-text-400.ttf`,
        revision: 'e7d9d76e624e',
      },
      {
        weight: 600,
        label: 'Полужирное',
        woff2: `${base}golos-text-600.woff2`,
        ttf: `${base}golos-text-600.ttf`,
        revision: 'dbb1d2e55033',
      },
      {
        weight: 700,
        label: 'Жирное',
        woff2: `${base}golos-text-700.woff2`,
        ttf: `${base}golos-text-700.ttf`,
        revision: '378403280c2e',
      },
    ],
  },
  {
    id: 'manrope',
    name: 'Manrope',
    cssFamily: 'Manrope',
    hint: 'Современные геометричные карточки',
    fallback: 'system-ui, sans-serif',
    license: 'OFL-1.1',
    licenseFile: 'licenses/OFL-manrope.txt',
    faces: [
      {
        weight: 400,
        label: 'Обычное',
        woff2: `${base}manrope-400.woff2`,
        ttf: `${base}manrope-400.ttf`,
        revision: '4f662fb25878',
      },
      {
        weight: 700,
        label: 'Жирное',
        woff2: `${base}manrope-700.woff2`,
        ttf: `${base}manrope-700.ttf`,
        revision: '46776250729a',
      },
    ],
  },
  {
    id: 'montserrat',
    name: 'Montserrat',
    cssFamily: 'Montserrat',
    hint: 'Широкие заголовки и постеры',
    fallback: 'system-ui, sans-serif',
    license: 'OFL-1.1',
    licenseFile: 'licenses/OFL-montserrat.txt',
    faces: [
      {
        weight: 400,
        label: 'Обычное',
        woff2: `${base}montserrat-400.woff2`,
        ttf: `${base}montserrat-400.ttf`,
        revision: '8e0230925909',
      },
      {
        weight: 700,
        label: 'Жирное',
        woff2: `${base}montserrat-700.woff2`,
        ttf: `${base}montserrat-700.ttf`,
        revision: 'dc476f8c6541',
      },
    ],
  },
  {
    id: 'pt-sans',
    name: 'PT Sans',
    cssFamily: 'PT Sans',
    hint: 'Нейтральные информационные материалы',
    fallback: 'system-ui, sans-serif',
    license: 'OFL-1.1',
    licenseFile: 'licenses/OFL-pt-sans.txt',
    faces: [
      {
        weight: 400,
        label: 'Обычное',
        woff2: `${base}pt-sans-400.woff2`,
        ttf: `${base}pt-sans-400.ttf`,
        revision: '3dc88c0914ae',
      },
      {
        weight: 700,
        label: 'Жирное',
        woff2: `${base}pt-sans-700.woff2`,
        ttf: `${base}pt-sans-700.ttf`,
        revision: '2d12afeb7c4a',
      },
    ],
  },
  {
    id: 'pt-serif',
    name: 'PT Serif',
    cssFamily: 'PT Serif',
    hint: 'Классическое оформление с засечками',
    fallback: 'Georgia, serif',
    license: 'OFL-1.1',
    licenseFile: 'licenses/OFL-pt-serif.txt',
    faces: [
      {
        weight: 400,
        label: 'Обычное',
        woff2: `${base}pt-serif-400.woff2`,
        ttf: `${base}pt-serif-400.ttf`,
        revision: 'c93cadf035e5',
      },
      {
        weight: 700,
        label: 'Жирное',
        woff2: `${base}pt-serif-700.woff2`,
        ttf: `${base}pt-serif-700.ttf`,
        revision: '107722b4d250',
      },
    ],
  },
  {
    id: 'roboto-condensed',
    name: 'Roboto Condensed',
    cssFamily: 'Roboto Condensed',
    hint: 'Узкие плашки и длинные заголовки',
    fallback: 'system-ui, sans-serif',
    license: 'OFL-1.1',
    licenseFile: 'licenses/OFL-roboto-condensed.txt',
    faces: [
      {
        weight: 400,
        label: 'Обычное',
        woff2: `${base}roboto-condensed-400.woff2`,
        ttf: `${base}roboto-condensed-400.ttf`,
        revision: '07f80de2b45c',
      },
      {
        weight: 700,
        label: 'Жирное',
        woff2: `${base}roboto-condensed-700.woff2`,
        ttf: `${base}roboto-condensed-700.ttf`,
        revision: '9dd79ac587c3',
      },
    ],
  },
];

export const DEFAULT_FONT_ID = 'golos-text';
export const DEFAULT_FONT_WEIGHT = 600;

export function getFamily(id: string): FontFamily {
  return FONT_FAMILIES.find((f) => f.id === id) ?? FONT_FAMILIES[0]!;
}

/** Возвращает начертание; если запрошенного веса нет — ближайшее по весу. */
export function getFace(familyId: string, weight: number): FontFace {
  const family = getFamily(familyId);
  const exact = family.faces.find((f) => f.weight === weight);
  if (exact) return exact;
  return family.faces.reduce((best, f) =>
    Math.abs(f.weight - weight) < Math.abs(best.weight - weight) ? f : best,
  );
}

export function isKnownFontId(id: string): boolean {
  return FONT_FAMILIES.some((f) => f.id === id);
}

/** Строка font-family для CSS с локальным семейством и запасным вариантом. */
export function cssFontStack(familyId: string): string {
  const family = getFamily(familyId);
  return `"${family.cssFamily}", ${family.fallback}`;
}

/** Генерирует @font-face для всех семейств каталога — только локальные файлы. */
export function fontFaceCss(assetBase = ''): string {
  return FONT_FAMILIES.flatMap((family) =>
    family.faces.map(
      (face) => `@font-face {
  font-family: "${family.cssFamily}";
  src: url("${assetBase}${face.woff2}") format("woff2");
  font-weight: ${face.weight};
  font-style: normal;
  font-display: swap;
}`,
    ),
  ).join('\n');
}
